import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { TicketAttachment } from '../../../../../api/types/dasTickets.types';
import { attachmentsService } from '../../../../../api/services/das/attachmentsService';

interface TicketAttachmentsProps {
  attachments: TicketAttachment[];
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileTypeColor = (fileType: string): string => {
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) return '#ef4444';
  if (type.includes('word') || type.includes('doc')) return '#3b82f6';
  if (type.includes('excel') || type.includes('sheet')) return '#10b981';
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg'))
    return '#8b5cf6';
  if (type.includes('zip') || type.includes('rar')) return '#f59e0b';
  return '#64748b';
};

const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
};

export const TicketAttachments: React.FC<TicketAttachmentsProps> = ({ attachments }) => {
  // Preview state
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  // Handle preview - fetch blob and create URL (without triggering download)
  const handlePreview = async (attachment: TicketAttachment) => {
    try {
      setLoadingId(attachment.id.toString());
      const blobUrl = await attachmentsService.getPreviewUrl(attachment.id, attachment.fileType);
      setPreviewDocument({
        url: blobUrl,
        fileName: attachment.fileName,
        fileType: attachment.fileType,
      });
    } catch (error: any) {
      console.error('Failed to load preview:', error);
      const errorMessage = error?.response?.data?.message
        || error?.message
        || 'Unknown error occurred';
      alert(`Failed to load document preview: ${errorMessage}`);
    } finally {
      setLoadingId(null);
    }
  };

  // Handle download
  const handleDownload = async (attachment: TicketAttachment) => {
    try {
      setLoadingId(attachment.id.toString());
      await attachmentsService.download(attachment.id, attachment.fileName);
    } catch (error) {
      console.error('Failed to download:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setLoadingId(null);
    }
  };

  // Close preview and cleanup blob URL
  const closePreview = () => {
    if (previewDocument?.url) {
      window.URL.revokeObjectURL(previewDocument.url);
    }
    setPreviewDocument(null);
  };

  if (!attachments || attachments.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <IconSystem.file size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          No attachments available for this ticket.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Attachments ({attachments.length})
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Files attached to this ticket
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: '0.5rem',
              padding: '1rem',
              border: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
          >
            {/* File Icon */}
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.5rem',
                background: getFileTypeColor(attachment.fileType),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <IconSystem.file size={24} />
              <div style={{ fontSize: '0.625rem', fontWeight: 600, marginTop: '0.25rem' }}>
                {getFileExtension(attachment.fileName)}
              </div>
            </div>

            {/* File Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '0.25rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={attachment.documentTitle}
              >
                {attachment.documentTitle}
              </div>
              {attachment.documentDescription && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.25rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={attachment.documentDescription}
                >
                  {attachment.documentDescription}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.75rem',
                  color: 'var(--text-tertiary)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontFamily: 'monospace' }}>{attachment.fileName}</span>
                <span>•</span>
                <span>{formatFileSize(attachment.fileSize)}</span>
                <span>•</span>
                <span>Uploaded by {attachment.uploadedBy}</span>
                <span>•</span>
                <span>{formatDate(attachment.uploadedAt)}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              {/* Preview Button - Only for PDFs and Images */}
              {(attachment.fileType.startsWith('image/') || attachment.fileType.includes('pdf')) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(attachment);
                  }}
                  disabled={loadingId === attachment.id.toString()}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  title="Preview document"
                >
                  <IconSystem.eye size={16} />
                  Preview
                </button>
              )}

              {/* Download Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(attachment);
                }}
                disabled={loadingId === attachment.id.toString()}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: loadingId === attachment.id.toString() ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'opacity 0.2s',
                  opacity: loadingId === attachment.id.toString() ? 0.7 : 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = loadingId === attachment.id.toString() ? '0.7' : '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = loadingId === attachment.id.toString() ? '0.7' : '1')}
                title="Download file"
              >
                <IconSystem.download size={16} />
                {loadingId === attachment.id.toString() ? 'Loading...' : 'Download'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <div
          onClick={closePreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 11000,
            padding: '2rem',
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              color: 'white',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Document Preview</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                {previewDocument.fileName}
              </p>
            </div>
            <button
              onClick={closePreview}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
              title="Close preview"
            >
              <IconSystem.x size={24} />
            </button>
          </div>

          {/* Preview Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              background: 'white',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {previewDocument.fileType.startsWith('image/') ? (
              <img
                src={previewDocument.url}
                alt={previewDocument.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : previewDocument.fileType.includes('pdf') ? (
              <iframe
                src={previewDocument.url}
                title={previewDocument.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <IconSystem.file size={48} style={{ marginBottom: '1rem' }} />
                <p>Preview not available for this file type</p>
              </div>
            )}
          </div>

          {/* Preview Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              marginTop: '1rem',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(previewDocument.url, '_blank');
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <IconSystem.download size={16} />
              Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketAttachments;
